import {
  ENTRIES,
  ENTRY,
  barOf,
  entry,
  isAsked,
  type Condition,
  type Entry,
  type Requiredness,
} from '@/james-playground/aa-family-claim/claim/registry';
import {
  isEmpty,
  text,
  valueOf,
  type Facts,
} from '@/james-playground/aa-family-claim/claim/facts';
import {
  AREAS,
  ASKS,
  BATCH_GATES,
  DOCUMENTS,
  EFFORT,
  OPENING_VOICE,
  VOICE_PROMPTS,
  VOICE_THRESHOLD,
  type Area,
  type AreaId,
  type Ask,
  type DocumentKind,
  type VoicePrompt,
} from '@/james-playground/aa-family-claim/claim/asks';

/**
 * The gather loop's rules, all deterministic. What applies, what is still
 * open, when the claim is ready, which step is quickest next, and whether the
 * claim belongs on this form at all. The AI proposes answers; these rules decide
 * everything else.
 */

// ---- Conditions ----------------------------------------------------------

/** Nationalities that Q8.2 follows up. Irish citizens are not asked (rule R6.5). */
const EEA_OR_SWISS = new Set(
  [
    'Austrian', 'Belgian', 'Bulgarian', 'Croatian', 'Cypriot', 'Czech', 'Danish',
    'Dutch', 'Estonian', 'Finnish', 'French', 'German', 'Greek', 'Hungarian',
    'Italian', 'Latvian', 'Lithuanian', 'Luxembourgish', 'Maltese', 'Polish',
    'Portuguese', 'Romanian', 'Slovak', 'Slovenian', 'Spanish', 'Swedish',
    'Icelandic', 'Liechtensteiner', 'Norwegian', 'Swiss',
  ].map((n) => n.toLowerCase()),
);

/** Postcode areas, for routing. Production uses a postcode lookup. */
const POSTCODE_AREA = (postcode: string) =>
  postcode.trim().toUpperCase().match(/^[A-Z]{1,2}/)?.[0] ?? '';
const SCOTLAND = new Set([
  'AB', 'DD', 'DG', 'EH', 'FK', 'G', 'HS', 'IV', 'KA', 'KW', 'KY', 'ML', 'PA',
  'PH', 'TD', 'ZE',
]);
const WALES = new Set(['CF', 'LD', 'LL', 'NP', 'SA']);

export type Country = 'england' | 'wales' | 'scotland' | 'northernIreland' | 'unknown';

export const countryOf = (postcode: string): Country => {
  const area = POSTCODE_AREA(postcode);
  if (!area) return 'unknown';
  if (area === 'BT') return 'northernIreland';
  if (SCOTLAND.has(area)) return 'scotland';
  if (WALES.has(area)) return 'wales';
  return 'england';
};

/** Three-valued: true, false, or not yet known. */
export type Tri = boolean | undefined;

const and = (values: Tri[]): Tri =>
  values.includes(false) ? false : values.includes(undefined) ? undefined : true;
const or = (values: Tri[]): Tri =>
  values.includes(true) ? true : values.includes(undefined) ? undefined : false;

export const evaluate = (c: Condition, facts: Facts): Tri => {
  if ('all' in c) return and(c.all.map((x) => evaluate(x, facts)));
  if ('any' in c) return or(c.any.map((x) => evaluate(x, facts)));
  if ('fn' in c) {
    const postcode = text(facts, 'postcode');
    return postcode ? countryOf(postcode) === 'wales' : undefined;
  }
  // A question that is never asked decides nothing, so what hangs on it is
  // never asked either: under special rules, Q39's tables drop with Q39.
  const decider = ENTRY[c.id];
  if (decider?.when && evaluate(decider.when, facts) === false) return false;
  // Skipping the quiet line carries on with the standard claim: the care
  // questions are still asked, and the adviser raises the quicker route.
  const value =
    c.id === 'specialRules' && facts.specialRules?.state === 'skipped'
      ? 'No'
      : valueOf(facts, c.id);
  // A multi-choice answer is a list: "is No" means it includes No.
  const list = Array.isArray(value) ? (value as string[]) : undefined;
  switch (c.op) {
    case 'eq':
      return value === undefined ? undefined : list ? list.includes(c.value) : value === c.value;
    case 'ne':
      return value === undefined ? undefined : list ? !list.includes(c.value) : value !== c.value;
    case 'has':
      return value === undefined
        ? undefined
        : Array.isArray(value)
          ? (value as string[]).includes(c.value)
          : value === c.value;
    case 'filled':
      // A table column "applies" row by row; the table itself decides.
      return c.perRow ? true : value === undefined ? undefined : !isEmpty(value);
    case 'empty':
      return value === undefined ? undefined : isEmpty(value);
    case 'in':
      return value === undefined
        ? undefined
        : EEA_OR_SWISS.has(String(value).trim().toLowerCase());
  }
};

// ---- Status of an entry --------------------------------------------------

export type Status =
  | 'not_applicable'
  | 'pending'
  | 'open'
  | 'proposed'
  | 'conflict'
  | 'confirmed'
  | 'skipped';

export const applies = (e: Entry, facts: Facts): Tri =>
  e.when ? evaluate(e.when, facts) : true;

export const statusOf = (e: Entry, facts: Facts): Status => {
  const applicable = applies(e, facts);
  if (applicable === false) return 'not_applicable';
  const fact = facts[e.id];
  if (fact) return fact.state;
  return applicable === undefined ? 'pending' : 'open';
};

const WEIGHT: Record<Requiredness, number> = {
  required_to_process: 3,
  needed_for_decision: 2,
  strengthens_claim: 1,
  optional: 0,
};

const ASKED = ENTRIES.filter(isAsked);

/** Entries still open for a bar: what stands between the claim and that bar. */
export const openFor = (facts: Facts, bar: 1 | 2): Entry[] =>
  ASKED.filter((e) => barOf(e) === bar && statusOf(e, facts) === 'open');

/** Ready to send: everything bar 1 needs is answered, proposed or skipped. */
export const isReady = (facts: Facts) => openFor(facts, 1).length === 0;

/** Answers the Check step must confirm before sending. */
export const toCheck = (facts: Facts): Entry[] =>
  ASKED.filter((e) => {
    const s = statusOf(e, facts);
    return s === 'proposed' || s === 'conflict';
  });

/** Skipped answers: the Care Adviser's "To talk through" list. */
export const skipped = (facts: Facts): Entry[] =>
  ASKED.filter((e) => statusOf(e, facts) === 'skipped');

// ---- Asks and areas ------------------------------------------------------

export const openIn = (ask: Ask, facts: Facts, bar?: 1 | 2): Entry[] =>
  ask.entries
    .map(entry)
    .filter(
      (e) =>
        isAsked(e) &&
        statusOf(e, facts) === 'open' &&
        (bar === undefined || barOf(e) === bar),
    );

/** The bar the loop is working toward. */
export const currentBar = (facts: Facts): 1 | 2 => (isReady(facts) ? 2 : 1);

export type AreaState = 'not_started' | 'in_progress' | 'done' | 'hidden';

export type AreaSummary = {
  area: Area;
  state: AreaState;
  /** Open asks in the area: the small "2 to go". Never a count of form boxes. */
  toGo: number;
  asks: Ask[];
};

export const areaSummary = (id: AreaId, facts: Facts): AreaSummary => {
  const area = AREAS.find((a) => a.id === id) as Area;
  const asks = ASKS.filter((a) => a.area === id);
  const entries = asks.flatMap((a) => a.entries.map(entry)).filter(isAsked);
  const live = entries.filter((e) => statusOf(e, facts) !== 'not_applicable');
  // "Make it stronger" works toward bar 2; every other area toward bar 1.
  const bar = id === 'stronger' ? 2 : 1;
  const open = asks.filter((a) => openIn(a, facts, bar).length > 0);
  const started = live.some((e) => facts[e.id]);
  const anyForBar = live.some((e) => barOf(e) === bar);
  const state: AreaState = !live.length || !anyForBar
    ? 'hidden'
    : open.length === 0
      ? 'done'
      : started
        ? 'in_progress'
        : 'not_started';
  return { area, state, toGo: open.length, asks: open };
};

/** The hub's areas, in order, without Before we start. */
export const hubAreas = (facts: Facts): AreaSummary[] =>
  AREAS.filter((a) => a.id !== 'start')
    .filter((a) => a.id !== 'stronger' || isReady(facts))
    .map((a) => areaSummary(a.id, facts))
    .filter((s) => s.state !== 'hidden');

export type Progress = 'start' | 'good' | 'most' | 'ready' | 'stronger';

/** The hub headline, in words; never a total. */
export const progress = (facts: Facts): Progress => {
  if (isReady(facts))
    return openFor(facts, 2).length ? 'stronger' : 'ready';
  const bar1 = ASKED.filter(
    (e) => barOf(e) === 1 && statusOf(e, facts) !== 'not_applicable',
  );
  const done = bar1.filter((e) => statusOf(e, facts) !== 'open' && statusOf(e, facts) !== 'pending');
  const share = bar1.length ? done.length / bar1.length : 0;
  return share >= 0.6 ? 'most' : share >= 0.2 ? 'good' : 'start';
};

// ---- Ranking the next step ----------------------------------------------

/** What the family has already done, so a way in is not offered twice. */
export type Rounds = {
  openingDone: boolean;
  voiceDone: string[];
  documentsDone: DocumentKind[];
  batchDone: boolean;
};

export const NO_ROUNDS: Rounds = {
  openingDone: false,
  voiceDone: [],
  documentsDone: [],
  batchDone: false,
};

export type Step =
  | { kind: 'opening'; prompt: VoicePrompt; score: number; covers: Entry[] }
  | { kind: 'voice'; prompt: VoicePrompt; score: number; covers: Entry[] }
  | { kind: 'document'; doc: DocumentKind; score: number; covers: Entry[] }
  | { kind: 'batch'; score: number; covers: Entry[] }
  | { kind: 'ask'; ask: Ask; score: number; covers: Entry[] };

const EFFORT_VOICE = 6;
const EFFORT_DOCUMENT = 4;
const EFFORT_BATCH = 2;

const scoreOf = (covers: Entry[], effort: number) =>
  covers.reduce((sum, e) => sum + WEIGHT[e.requiredness], 0) / effort;

const hinted = (entries: Entry[], hint: string) =>
  entries.filter((e) => e.captureHints?.includes(hint as never));

/** A document is offered only when it plausibly exists (spec 3.4). */
const plausible = (doc: DocumentKind, facts: Facts): boolean => {
  if (doc === 'prescription') return statusOf(entry('illnessesTable'), facts) === 'open';
  if (doc === 'hospital_letter')
    return ['seenProfessional', 'surgeryWaitingList', 'hadTests', 'currentStay', 'previousStayLastSixWeeks'].some(
      (id) => {
        const v = valueOf(facts, id);
        return v === undefined || v === 'Yes';
      },
    );
  const helpers = valueOf(facts, 'helpersTable');
  return JSON.stringify(helpers ?? '').match(/carer|agency/i) !== null;
};

/**
 * Every way forward, best first. Before the claim is ready only bar 1 answers
 * count; after, only bar 2. The opening voice note always comes first.
 */
export const nextSteps = (facts: Facts, rounds: Rounds): Step[] => {
  const bar = currentBar(facts);
  const open = openFor(facts, bar);
  if (!rounds.openingDone && bar === 1) {
    return [{ kind: 'opening', prompt: OPENING_VOICE, score: Infinity, covers: hinted(open, 'voice') }];
  }
  const steps: Step[] = [];

  for (const a of ASKS) {
    if (a.area === 'start') continue;
    const covers = openIn(a, facts, bar);
    if (covers.length) steps.push({ kind: 'ask', ask: a, covers, score: scoreOf(covers, EFFORT[a.kind]) });
  }

  if (bar === 1) {
    for (const prompt of VOICE_PROMPTS) {
      if (rounds.voiceDone.includes(prompt.id)) continue;
      const inArea = new Set(ASKS.filter((a) => a.area === prompt.area).flatMap((a) => a.entries));
      const covers = hinted(open.filter((e) => inArea.has(e.id)), 'voice');
      if (covers.length >= VOICE_THRESHOLD)
        steps.push({ kind: 'voice', prompt, covers, score: scoreOf(covers, EFFORT_VOICE) });
    }
    for (const { kind } of DOCUMENTS) {
      if (rounds.documentsDone.includes(kind) || !plausible(kind, facts)) continue;
      const covers = hinted(open, kind);
      if (covers.length) steps.push({ kind: 'document', doc: kind, covers, score: scoreOf(covers, EFFORT_DOCUMENT) });
    }
    if (!rounds.batchDone) {
      const covers = BATCH_GATES.map((g) => entry(g.id)).filter((e) => statusOf(e, facts) === 'open');
      if (covers.length) steps.push({ kind: 'batch', covers, score: scoreOf(covers, EFFORT_BATCH) });
    }
  }

  const areaRank = (s: Step) => {
    const area = s.kind === 'ask' ? s.ask.area : s.kind === 'voice' ? s.prompt.area : 'health';
    return AREAS.findIndex((a) => a.id === area);
  };
  return steps.sort((x, y) => y.score - x.score || areaRank(x) - areaRank(y));
};

// ---- Pathways ------------------------------------------------------------

export type Signer =
  | 'claimant'
  | 'specialRules'
  | 'appointee'
  | 'attorney'
  | 'deputy'
  | 'scottish'
  | 'organisation'
  | 'applying'
  | 'unsure';

const SIGNER_BY_CATEGORY: [RegExp, Signer][] = [
  [/special rules/i, 'specialRules'],
  [/appointee, appointed/i, 'appointee'],
  [/Power of Attorney/i, 'attorney'],
  [/Deputy/i, 'deputy'],
  [/Tutor|curator|judicial/i, 'scottish'],
  [/Corporate/i, 'organisation'],
  [/want to be appointed/i, 'applying'],
];

export const signerOf = (facts: Facts): Signer => {
  const someoneElse = text(facts, 'signingForSomeoneElse');
  if (someoneElse === 'No') return 'claimant';
  if (someoneElse !== 'Yes') return 'unsure';
  const category = text(facts, 'signerCategory');
  return SIGNER_BY_CATEGORY.find(([re]) => re.test(category))?.[1] ?? 'unsure';
};

export type Pathway = {
  specialRules: boolean;
  /** In a hospital, hospice or care home now or in the last 6 weeks. */
  inCare: boolean;
  placeType: string;
  publiclyFunded: boolean;
  selfClaim: boolean;
  signer: Signer;
};

export const pathwayOf = (facts: Facts, selfClaim: boolean): Pathway => ({
  specialRules: text(facts, 'specialRules') === 'Yes',
  inCare:
    text(facts, 'currentStay') === 'Yes' ||
    text(facts, 'previousStayLastSixWeeks') === 'Yes',
  placeType: text(facts, 'currentStayPlaceType') || text(facts, 'previousStayPlaceType'),
  publiclyFunded: text(facts, 'publiclyFundedStay') === 'Yes',
  selfClaim,
  signer: selfClaim ? 'claimant' : signerOf(facts),
});

// ---- Routing (spec 1) -----------------------------------------------------

export type Handoff =
  | 'scotland'
  | 'northernIreland'
  | 'abroad'
  | 'underSpa'
  | 'hasAa'
  | 'hasPip'
  | 'pipEnded'
  | 'bereavement';

const addMonths = (date: Date, months: number) => {
  const d = new Date(date);
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + months);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, last));
  return d;
};

/**
 * The date someone reaches State Pension age, from the official timetable
 * (rule R2.1). Born 6 Oct 1954 to 5 Apr 1960: their 66th birthday. Born 6 Apr
 * 1960 to 5 Mar 1961: 66 years plus one month per birth month. From 6 Mar 1961:
 * 67. Earlier dates of birth reached it long before any claim made here.
 */
export const statePensionDate = (dob: Date): Date => {
  // Local dates throughout: a UTC cutoff can tip a birthday into the wrong band.
  const on = (y: number, m: number, d: number) => dob >= new Date(y, m - 1, d);
  if (!on(1954, 10, 6)) return addMonths(dob, 65 * 12);
  if (!on(1960, 4, 6)) return addMonths(dob, 66 * 12);
  if (!on(1961, 3, 6)) {
    // One extra month for each monthly band after 5 April 1960.
    let extra = 1;
    let bandEnd = new Date(1960, 4, 6);
    while (dob >= bandEnd && extra < 11) {
      extra += 1;
      bandEnd = addMonths(bandEnd, 1);
    }
    return addMonths(dob, 66 * 12 + extra);
  }
  return addMonths(dob, 67 * 12);
};

/** Parse "19/03/1943" or "1943-03-19". */
export const parseDate = (value: string): Date | undefined => {
  const uk = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (uk) return new Date(Number(uk[3]), Number(uk[2]) - 1, Number(uk[1]));
  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  return undefined;
};

/** The first route that takes the claim off the AA1, or null to carry on. */
export const routeOf = (facts: Facts, today: Date): Handoff | null => {
  const country = countryOf(text(facts, 'postcode'));
  if (country === 'scotland') return 'scotland';
  if (country === 'northernIreland') return 'northernIreland';
  if (text(facts, 'normallyLiveGb') === 'No') return 'abroad';
  const dob = parseDate(text(facts, 'dob'));
  if (dob && statePensionDate(dob) > today) return 'underSpa';
  const award = text(facts, 'existingDisabilityAward');
  if (award === 'Attendance Allowance') return 'hasAa';
  if (award.startsWith('PIP, DLA')) return 'hasPip';
  if (award.startsWith('PIP or Adult')) return 'pipEnded';
  return null;
};

/** Six months after care needs began, when payment could start (rule R5.1). */
export const qualifyingDate = (facts: Facts): Date | undefined => {
  if (text(facts, 'specialRules') === 'Yes') return undefined;
  const started = parseDate(text(facts, 'careStartDate'));
  return started ? addMonths(started, 6) : undefined;
};

/** The 6-week deadline that keeps the requested-form date (rule R10.3). */
export const sendBy = (facts: Facts): Date | undefined => {
  const requested = parseDate(text(facts, 'claimFormRequestedOn'));
  if (!requested) return undefined;
  const d = new Date(requested);
  d.setDate(d.getDate() + 42);
  return d;
};
